import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X, Minus, Plus, Trash2, ArrowLeft, Loader2, Package } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/App";
import { copy, LOGO } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
    { key: "gift", variants: 4 },
    { key: "tshirt", price: 35, sizes: ["M", "L", "XL"], variants: 8 },
    { key: "cap", price: 25 },
    { key: "beanie", price: 25, variants: 4 },
    { key: "apron", price: 25, variants: 4 },
];
const VARIANTS = [1, 2, 3, 4, 5, 6, 7, 8];

const PRODUCT_INFO = {
    tshirt_01: {
        name: "Tiriamo a Campari",
        photos: ["/media/tshirt-01-a.webp", "/media/tshirt-01-b.webp", "/media/tshirt-01-c.webp"],
    },
    tshirt_02: {
        name: "Siamo Gente Per Bere",
        photos: ["/media/tshirt-02-a.webp", "/media/tshirt-02-b.webp", "/media/tshirt-02-c.webp"],
    },
    tshirt_03: {
        name: "Il Bere Vincerà Sul Male",
        photos: ["/media/tshirt-03-a.webp", "/media/tshirt-03-b.webp", "/media/tshirt-03-c.webp"],
    },
    tshirt_04: {
        name: "Reggo Più l'Alcool che le Persone",
        photos: ["/media/tshirt-04-a.webp", "/media/tshirt-04-b.webp", "/media/tshirt-04-c.webp"],
    },
    tshirt_05: {
        name: "Guida Poco Che Devi Bere",
        photos: ["/media/tshirt-05-a.webp", "/media/tshirt-05-b.webp", "/media/tshirt-05-c.webp"],
    },
    tshirt_06: {
        name: "Caffè Corretti Negroni Sbagliati",
        photos: ["/media/tshirt-06-a.webp", "/media/tshirt-06-b.webp", "/media/tshirt-06-c.webp"],
    },
    cap_01: {
        name: "Secondo la Chimica l'Alcool è una Soluzione",
        photos: ["/media/cap-01-b.webp", "/media/cap-01-a.webp"],
    },
    cap_02: {
        name: "Guida Poco Che Devi Bere",
        photos: ["/media/cap-02-a.webp", "/media/cap-02-b.webp", "/media/cap-02-c.webp"],
    },
    cap_03: {
        name: "Caffè Corretti e Negroni Sbagliati",
        photos: ["/media/cap-03-a.webp", "/media/cap-03-b.webp", "/media/cap-03-c.webp"],
    },
    cap_04: {
        name: "Il Bere Vincerà sul Male",
        photos: ["/media/cap-04-a.webp", "/media/cap-04-b.webp", "/media/cap-04-c.webp"],
    },
    cap_05: {
        name: "Powered by Champagne",
        photos: ["/media/cap-05-a.webp", "/media/cap-05-b.webp"],
    },
    cap_06: {
        name: "Tiriamo a Campari",
        photos: ["/media/cap-06-a.webp", "/media/cap-06-b.webp"],
    },
    tshirt_07: {
        name: "Secondo la Chimica l'Alcool è una Soluzione",
        photos: ["/media/tshirt-07-a.webp", "/media/tshirt-07-b.webp", "/media/tshirt-07-c.webp"],
    },
    tshirt_08: {
        name: "Nato Sbagliato",
        photos: ["/media/tshirt-08-a.webp", "/media/tshirt-08-b.webp", "/media/tshirt-08-c.webp"],
    },
    gift_01: { name: "Gift Card The Market", price: 50, desc: "giftCardDesc", photos: ["/media/gift-50.webp"] },
    gift_02: { name: "Gift Card The Market", price: 100, desc: "giftCardDesc", photos: ["/media/gift-100.webp"] },
    gift_03: { name: "Buono Regalo Aperitivo per Due", price: 80, desc: "aperitivoDesc", photos: ["/media/piatti-taglieri.jpg"] },
    gift_04: { name: "Buono Regalo Cena per Due", price: 140, desc: "cenaDesc", photos: ["/media/buono-cena.webp"] },
    beanie_01: { name: "Secondo la Chimica l'Alcool è una Soluzione", photos: ["/media/beanie-01-a.webp", "/media/beanie-01-b.webp"] },
    beanie_02: { name: "Tiriamo a Campari", photos: ["/media/beanie-02-a.webp", "/media/beanie-02-b.webp"] },
    beanie_03: { name: "Il Bere Vincerà Sul Male", photos: ["/media/beanie-03-a.webp", "/media/beanie-03-b.webp"] },
    beanie_04: { name: "Siamo Gente Per Bere", photos: ["/media/beanie-04-a.webp", "/media/beanie-04-b.webp"] },
    apron_01: { name: "Tiriamo a Campari", photos: ["/media/apron-01.webp"] },
    apron_02: { name: "Siamo Gente Per Bere", photos: ["/media/apron-02.webp"] },
    apron_03: { name: "Il Bere Vincerà Sul Male", photos: ["/media/apron-03.webp"] },
    apron_04: { name: "Reggo Più l'Alcool che le Persone", photos: ["/media/apron-04.webp"] },
    umbrella_01: { name: "Tiriamo a Campari" },
};

const inputCls =
    "w-full rounded-xl bg-white/[0.04] border border-white/15 focus:border-crimson focus:outline-none transition-colors px-4 py-3 text-sm text-white placeholder:text-neutral-500";

const ProductCard = ({ cat, variant, c, onAdd }) => {
    const [size, setSize] = useState("M");
    const [photoIdx, setPhotoIdx] = useState(0);
    const num = String(variant).padStart(2, "0");
    const info = PRODUCT_INFO[`${cat.key}_${num}`];
    const displayName = info?.name || `${c.cats[cat.key]} · ${c.model} ${num}`;
    const comingSoon = Boolean(info?.comingSoon);
    const price = info?.price ?? cat.price;
    const desc = info?.desc ? c[info.desc] : null;
    return (
        <div data-testid={`product-${cat.key}-${num}`} className={`rounded-2xl border border-white/10 bg-card overflow-hidden group ${comingSoon ? "opacity-75" : ""}`}>
            <div className="relative aspect-square bg-coal overflow-hidden">
                {info?.photos ? (
                    <>
                        <img
                            src={info.photos[photoIdx]}
                            alt={displayName}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            loading="lazy"
                        />
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                            {info.photos.map((_, pi) => (
                                <button
                                    key={pi}
                                    data-testid={`photo-dot-${cat.key}-${num}-${pi}`}
                                    onClick={() => setPhotoIdx(pi)}
                                    aria-label={`foto ${pi + 1}`}
                                    className={`w-2 h-2 rounded-full transition-colors ${photoIdx === pi ? "bg-crimson" : "bg-white/40 hover:bg-white/70"}`}
                                />
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <span className="absolute font-display font-extrabold text-8xl text-white/[0.05] select-none">{num}</span>
                        <img src={LOGO} alt="The Market" className="absolute inset-0 m-auto w-2/3 opacity-90 transition-transform duration-500 group-hover:scale-105" />
                    </>
                )}
                {cat.key !== "gift" && (
                    <span className={`absolute top-3 left-3 text-[10px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 backdrop-blur border ${
                        comingSoon
                            ? "text-white border-white/40 bg-white/10"
                            : "text-brass border-brass/40 bg-noir/60"
                    }`}>
                        {comingSoon ? c.comingSoon : c.preorder}
                    </span>
                )}
            </div>
            <div className="p-4">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-heading font-bold text-sm">
                        {displayName}
                    </h3>
                    <span className="font-mono text-crimson-bright font-semibold text-sm">{price}.–</span>
                </div>
                {desc && <p className="mt-1.5 text-xs text-neutral-400 leading-relaxed">{desc}</p>}
                {cat.sizes && !comingSoon && (
                    <div className="mt-3 flex gap-1.5">
                        {cat.sizes.map((s) => (
                            <button
                                key={s}
                                data-testid={`size-${cat.key}-${num}-${s}`}
                                onClick={() => setSize(s)}
                                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                                    size === s ? "bg-crimson border-crimson text-white" : "border-white/15 text-neutral-400 hover:text-white"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
                <button
                    data-testid={`add-${cat.key}-${num}`}
                    onClick={() => onAdd(cat, variant, cat.sizes ? size : "")}
                    disabled={comingSoon}
                    className={`mt-3 w-full rounded-full text-xs font-bold uppercase tracking-widest py-2.5 transition-colors ${
                        comingSoon
                            ? "bg-white/10 text-neutral-400 cursor-not-allowed"
                            : "bg-white text-noir hover:bg-crimson hover:text-white"
                    }`}
                >
                    {comingSoon ? c.comingSoon : c.add}
                </button>
            </div>
        </div>
    );
};

export default function ShopPage() {
    const { lang, setLang } = useLang();
    const c = copy[lang].shop;
    const [cart, setCart] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("tm_cart") || "[]");
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);
    const [delivery, setDelivery] = useState("pickup");
    const [cust, setCust] = useState({ name: "", email: "", phone: "" });
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        localStorage.setItem("tm_cart", JSON.stringify(cart));
    }, [cart]);

    const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const shipping = delivery === "shipping" && subtotal < 100 ? 4 : 0;
    const total = subtotal + shipping;
    const count = cart.reduce((s, i) => s + i.qty, 0);

    const add = (cat, variant, size) => {
        const num = String(variant).padStart(2, "0");
        const lookup = `${cat.key}_${num}`;
        const id = size ? `${lookup}_${size}` : lookup;
        const info = PRODUCT_INFO[lookup];
        const displayName = info?.name || `${c.cats[cat.key]} · ${c.model} ${num}`;
        setCart((prev) => {
            const ex = prev.find((p) => p.id === id);
            if (ex) return prev.map((p) => (p.id === id ? { ...p, qty: p.qty + 1 } : p));
            return [
                ...prev,
                { id, lookup_key: lookup, name: displayName, size, price: info?.price ?? cat.price, qty: 1 },
            ];
        });
        setOpen(true);
    };

    const setQty = (id, delta) =>
        setCart((prev) =>
            prev.map((p) => (p.id === id ? { ...p, qty: Math.max(1, p.qty + delta) } : p))
        );

    const checkout = async () => {
        setPaying(true);
        try {
            const res = await axios.post(`${API}/shop/checkout`, {
                items: cart.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty, size: i.size })),
                delivery,
                customer: cust,
                origin_url: window.location.origin,
            });
            if (res.data.checkout_url) {
                localStorage.setItem("tm_cart", "[]");
                window.location.href = res.data.checkout_url;
            } else {
                toast.error(c.error);
                setPaying(false);
            }
        } catch {
            toast.error(c.error);
            setPaying(false);
        }
    };

    return (
        <div data-testid="shop-page" className="min-h-screen bg-noir">
            <header className="sticky top-0 z-40 bg-noir/85 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[72px] flex items-center justify-between gap-4">
                    <Link to="/" data-testid="shop-back-home" className="flex items-center gap-3 text-neutral-300 hover:text-white transition-colors">
                        <ArrowLeft size={18} />
                        <img src={LOGO} alt="The Market Lugano" className="h-7 w-auto" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <div data-testid="shop-lang-toggle" className="flex items-center rounded-full border border-white/15 overflow-hidden text-[11px] font-mono">
                            {["it", "en", "de", "fr"].map((l) => (
                                <button
                                    key={l}
                                    data-testid={`shop-lang-${l}`}
                                    onClick={() => setLang(l)}
                                    className={`px-2.5 py-1.5 uppercase transition-colors ${lang === l ? "bg-crimson text-white" : "text-neutral-400 hover:text-white"}`}
                                >
                                    {l}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="cart-open-button"
                            onClick={() => setOpen(true)}
                            className="relative rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white p-3"
                            aria-label={c.cart}
                        >
                            <ShoppingBag size={18} />
                            {count > 0 && (
                                <span data-testid="cart-count" className="absolute -top-1.5 -right-1.5 bg-white text-noir text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {count}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
                <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">The Market Shop</span>
                <h1 className="mt-3 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl">{c.title}</h1>
                <p className="mt-3 text-neutral-400 text-sm sm:text-base max-w-xl">{c.subtitle}</p>

                {CATS.map((cat) => (
                    <section key={cat.key} data-testid={`shop-cat-${cat.key}`} className="mt-14">
                        <div className="flex items-baseline gap-4 border-b border-brass/25 pb-2 mb-6">
                            <h2 className="font-serif italic text-2xl sm:text-3xl text-brass">{c.cats[cat.key]}</h2>
                            {cat.price && <span className="font-mono text-sm text-neutral-400">CHF {cat.price}.–</span>}
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {VARIANTS.slice(0, cat.variants || 6).map((v) => (
                                <ProductCard key={v} cat={cat} variant={v} c={c} onAdd={add} />
                            ))}
                        </div>
                    </section>
                ))}
            </main>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 bg-noir/70 backdrop-blur-sm"
                            onClick={() => setOpen(false)}
                        />
                        <motion.aside
                            data-testid="cart-drawer"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", duration: 0.3 }}
                            className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-coal border-l border-white/10 flex flex-col"
                        >
                            <div className="flex items-center justify-between p-5 border-b border-white/10">
                                <h3 className="font-display font-bold uppercase tracking-tight text-lg flex items-center gap-2">
                                    <ShoppingBag size={18} className="text-crimson" />
                                    {c.cart}
                                </h3>
                                <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-white p-1" aria-label="Close">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-5">
                                {cart.length === 0 ? (
                                    <div data-testid="cart-empty" className="text-center text-neutral-500 py-16">
                                        <Package size={32} className="mx-auto mb-3 opacity-50" />
                                        {c.empty}
                                    </div>
                                ) : (
                                    <ul className="space-y-3">
                                        {cart.map((i) => (
                                            <li key={i.id} data-testid={`cart-item-${i.id}`} className="flex items-center gap-3 rounded-xl bg-card border border-white/10 p-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold truncate">
                                                        {i.name}
                                                        {i.size && <span className="text-brass"> · {i.size}</span>}
                                                    </p>
                                                    <p className="text-xs font-mono text-neutral-400">CHF {i.price}.–</p>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <button data-testid={`qty-minus-${i.id}`} onClick={() => setQty(i.id, -1)} className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-neutral-300 hover:border-crimson" aria-label="minus">
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="w-6 text-center text-sm font-bold">{i.qty}</span>
                                                    <button data-testid={`qty-plus-${i.id}`} onClick={() => setQty(i.id, 1)} className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center text-neutral-300 hover:border-crimson" aria-label="plus">
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                                <button data-testid={`remove-${i.id}`} onClick={() => setCart((p) => p.filter((x) => x.id !== i.id))} className="text-neutral-500 hover:text-crimson-bright p-1" aria-label="remove">
                                                    <Trash2 size={15} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {cart.length > 0 && (
                                <div className="border-t border-white/10 p-5 space-y-4">
                                    <div data-testid="delivery-options" className="space-y-2">
                                        <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">{c.delivery}</p>
                                        <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${delivery === "pickup" ? "border-crimson bg-crimson/10" : "border-white/15"}`}>
                                            <input data-testid="delivery-pickup" type="radio" name="delivery" checked={delivery === "pickup"} onChange={() => setDelivery("pickup")} className="mt-1 accent-[#E60023]" />
                                            <span>
                                                <span className="block text-sm font-semibold">{c.pickup} · 0.–</span>
                                                <span className="block text-xs text-neutral-400 mt-0.5">{c.pickupNote}</span>
                                            </span>
                                        </label>
                                        <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${delivery === "shipping" ? "border-crimson bg-crimson/10" : "border-white/15"}`}>
                                            <input data-testid="delivery-shipping" type="radio" name="delivery" checked={delivery === "shipping"} onChange={() => setDelivery("shipping")} className="mt-1 accent-[#E60023]" />
                                            <span>
                                                <span className="block text-sm font-semibold">
                                                    {c.ship} · {subtotal >= 100 ? <span className="text-emerald-400">{c.freeShipOn}</span> : c.shipCost}
                                                </span>
                                                <span className="block text-xs text-neutral-400 mt-0.5">{c.freeShip}</span>
                                                {delivery === "shipping" && (
                                                    <span data-testid="shipping-address-note" className="block text-xs text-brass mt-1">{c.shipNote}</span>
                                                )}
                                            </span>
                                        </label>
                                    </div>

                                    <div className="grid gap-2.5">
                                        <input data-testid="cart-name-input" required value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} placeholder={c.name} className={inputCls} />
                                        <input data-testid="cart-email-input" required type="email" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} placeholder={c.email} className={inputCls} />
                                        <input data-testid="cart-phone-input" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} placeholder={c.phone} className={inputCls} />
                                    </div>

                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between text-neutral-400">
                                            <span>{c.subtotal}</span>
                                            <span className="font-mono">CHF {subtotal}.–</span>
                                        </div>
                                        <div className="flex justify-between text-neutral-400">
                                            <span>{c.delivery}</span>
                                            <span className="font-mono">{shipping === 0 ? "0.–" : `CHF ${shipping}.–`}</span>
                                        </div>
                                        <div data-testid="cart-total" className="flex justify-between font-heading font-bold text-lg pt-2 border-t border-white/10">
                                            <span>{c.total}</span>
                                            <span className="font-mono text-crimson-bright">CHF {total}.–</span>
                                        </div>
                                    </div>

                                    <button
                                        data-testid="checkout-button"
                                        onClick={checkout}
                                        disabled={paying || !cust.name || !cust.email}
                                        className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-crimson hover:bg-crimson-bright transition-colors text-white font-bold uppercase tracking-widest text-sm px-7 py-4 glow-red disabled:opacity-60"
                                    >
                                        {paying && <Loader2 size={16} className="animate-spin" />}
                                        {c.checkout}
                                    </button>
                                    <p className="text-center text-[11px] text-neutral-500 font-mono">{c.payNote}</p>
                                </div>
                            )}
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
